<script setup lang="ts">
import { computed } from 'vue';
import FileVault from '../components/file_vault/FileVault.vue';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import { isOfficeAddinHost } from '@/office/utils';
import { vaultDirectoryRoute } from '@/router/links';
import { useVaultStore } from '@/stores/vaultStore';

const office = isOfficeAddinHost();
// The office shell pads its own content and scrolls the page itself, so there
// the layout is static, edge to edge, and carries no breadcrumb bar.
const layoutProps = office
  ? { contentMaxWidth: '100%', contentPadding: '0', position: 'static' as const }
  : { contentMaxWidth: gaiaContentWidth.table };

const vaultStore = useVaultStore();

/**
 * The folders below the vault root, root-first, for the page's breadcrumb.
 * The vault's own trail stays off on this page (`:show-breadcrumbs="false"`), so
 * this is the only one. Read from the store path FileVault keeps current, so
 * it costs no request; the root itself is the vault crumb.
 */
const folderCrumbs = computed(() =>
  vaultStore.directoryPath
    .map(segment => ({ uid: segment.key.replace('dir-', ''), name: segment.name }))
    .filter(segment => segment.uid !== vaultStore.rootDirectoryUid)
);
</script>

<template>
  <Layout v-bind="layoutProps">
    <template v-if="!office" #breadcrumb>
      <n-breadcrumb-item :clickable="folderCrumbs.length > 0">
        <router-link v-if="folderCrumbs.length > 0" :to="{ name: 'file-vault' }">
          {{ navLabels.fileVault }}
        </router-link>
        <template v-else>{{ navLabels.fileVault }}</template>
      </n-breadcrumb-item>
      <n-breadcrumb-item
        v-for="(crumb, index) in folderCrumbs"
        :key="crumb.uid"
        :clickable="index < folderCrumbs.length - 1"
      >
        <router-link v-if="index < folderCrumbs.length - 1" :to="vaultDirectoryRoute(crumb.uid)">
          {{ crumb.name }}
        </router-link>
        <template v-else>{{ crumb.name }}</template>
      </n-breadcrumb-item>
    </template>

    <!-- The task pane is ~320px wide, so there the title keeps its own block
         above the vault — `FileVault`'s office height is measured against it. -->
    <n-flex v-if="office" vertical :size="6" class="office-header">
      <gaia-header :level="1" variant="display" size="dialog" :style="{ margin: 0 }">
        Contracts
      </gaia-header>
      <gaia-text size="control" tone="tertiary">
        Securely store, manage, and share your contracts and files with team members.
      </gaia-text>
    </n-flex>

    <!-- `FileVault` owns everything from the toolbar row down, because it is
         also mounted embedded (the review data room), where the host supplies
         its own heading. On the web the title goes in through its slot so it
         rides the same row as the search and "+ New" — the geometry every CM
         list page uses, so the header and the table sit in the same place when
         you switch between them. -->
    <FileVault :show-breadcrumbs="office" :file-drop="!office">
      <template v-if="!office" #title>
        <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
          Contracts
        </gaia-header>
      </template>
      <template v-if="!office" #actions>
        <router-link v-slot="{ href, navigate }" :to="{ name: 'upload-history' }" custom>
          <gaia-button tag="a" :href="href" size="medium" @click="navigate">
            {{ navLabels.uploadHistory }}
          </gaia-button>
        </router-link>
      </template>
    </FileVault>
  </Layout>
</template>

<style scoped>
.office-header {
  padding-bottom: 2px;
}
</style>
